<script setup>
import {getLogo} from "@/helpers/logo";
import {ref, onMounted, watch, provide, computed} from "vue";
import publicTrackersService from "@/services/public-trackers.service";
import {decodeDate} from "../../../helpers/date";
import {useStore} from "vue2-helpers/vuex";
import trackersService from "@/services/trackers.service";
import CustomFieldValue from "@/components/customFields/CustomFieldValue.vue";
import {useRoute} from "vue2-helpers/vue-router";
import profileService from "@/services/startup/profile.service";
import ProfileInfoForm from "@/components/startup/Profile.vue";
import {isCollinsId} from "@/constants/constants";
import TrackerQuestionsFill from "@/components/trackers/TrackerQuestionsFill.vue";
import trackersCompaniesService from "@/services/trackers-companies.service";
import useDebounce from "@/composables/useDebounce";
import trackersPublicService from "@/services/trackers-public.service";
import AnswersView from "@/components/trackers/application/AnswersView.vue";
import CollinsMenu from "@/components/trackers/application/CollinsTrackerMenu.vue";

const {id = null, trackerId, fromPreview = false} = defineProps(['id', 'trackerId','fromPreview']);
const emit = defineEmits(['login']);
const store = useStore();
const route = useRoute();
const debounce = useDebounce();

const info = ref(null);
const applicationStatus = ref(null);
const wantsApply = ref(false);
const canApply = ref(false);
const showApplicationForm = ref(false);
const showProfileModal = ref(false);
const showTermsModal = ref(false);
const loadingQuestions = ref(false);
const agreed = ref(false);
const sendingApplication = ref(false);
const questions = ref([]);
const trackerQuestionsFill = ref(null);

const isCollins = computed(() => {
    return info.value && isCollinsId(info.value.company_id);
})

watch(() => store.getters.isLoggedIn, async (value) => {
    if (value && !fromPreview) {
        await checkApplicationStatus();
    }
}, {immediate: true});

watch([wantsApply, () => store.getters.isLoggedIn], () => {
    if (store.getters.isLoggedIn && wantsApply.value) {
        checkCanApply()
    }
});

watch([applicationStatus, canApply], () => {
    if (canApply.value && applicationStatus.value !== 'applied') {
        showApplicationForm.value = true;
        getQuestions();
    }
});

onMounted( async () => {
    await getInfo();

    if (route.query['apply']) {
        applyClicked();
    }
});

function applyClicked() {
    if (fromPreview) {
        canApply.value = true;
    } else {
        wantsApply.value = true;

        if (store.getters.isLoggedIn) {
            checkCanApply();
        } else {
            emit('login');
        }
    }
}

async function getQuestions() {
    loadingQuestions.value = true;

    const {data} = await trackersService.getQuestions(trackerId);
    questions.value = data.map(question => ({
        ...question,
        error: '',
    }));

    if (store.getters.isStartup) {
        const {data: autosavedData} = await trackersCompaniesService.getAutosavedData(trackerId);

        if (autosavedData) {
            for (let i in questions.value) {
                if (questions.value[i].field.id in autosavedData) {
                    questions.value[i].value = autosavedData[questions.value[i].field.id];
                }
            }
        }
    }

    loadingQuestions.value = false;
}

async function checkCanApply() {
    if (info.value.need_complete_profile) {
        const {data} = await profileService.profileCompleted();

        if (!data) {
            if (store.state.user.is_admin) {
                showProfileModal.value = true;
            } else {
                alert("Profile info must be completed");
            }
        } else {
            canApply.value = true;
        }
    } else {
        canApply.value = true;
    }
}

async function checkApplicationStatus() {
    const {data} = await trackersPublicService.checkApplicationStatus(trackerId);
    applicationStatus.value = data;
}

function profileSaved() {
    showProfileModal.value = false;
    checkCanApply();
}

async function getInfo() {
    const {data} = fromPreview
        ? await trackersPublicService.getApplicationInfo(trackerId)
        : await publicTrackersService.getApplicationInfo(id);
    info.value = data;

    store.state.isNotCollinsTracker = !isCollinsId(info.value.company_id);
}

function apply() {
    trackerQuestionsFill.value.validateAnswers();

    if (questions.value.some(question => question.error.length)) {
        return;
    }

    sendingApplication.value = true;

    if (trackerQuestionsFill.value.newFilesFieldsCount) {
        trackerQuestionsFill.value.saveFiles();
    } else {
        sendApplication();
    }
}

async function sendApplication() {
    await trackersPublicService.apply(trackerId, {answers: questions.value});

    applicationStatus.value = 'applied';
    showApplicationForm.value = false;
    sendingApplication.value = false;
}

async function deleteSubmission() {
    if (!confirm("Delete submission?")) {
        return;
    }

    await trackersPublicService.deleteSubmission(trackerId);
    window.location.reload();
}

function saveAnswer(answer) {
    debounce(async () => {
        trackersCompaniesService.autoSaveAnswer(trackerId, answer);
    });
}

provide('questions', questions);
provide('loadingQuestions', loadingQuestions);
</script>

<template>
    <div v-if="info !== null" :class="{'application-rtx': isCollins}">
        <div class="topbar">
            <div class="topbar__inner d-flex align-items-center">
                <div class="image-block image-block--lg mr-4">
                    <img :src="getLogo(info.company_logo)" alt="img">
                </div>

                <div>
                    <h2 class="heading2">{{ info.company_name }}</h2>
                    <p v-if="isCollinsId(info.company_id)" class="text-big">Posted {{ decodeDate(info.posted, ['month-str']) }}</p>
                </div>

                <div v-if="isCollinsId(info.company_id)" class="topbar__inner__sidebar">
                    <div class="powered-by">Powered by <a href="" class="logo"></a></div>

                    <CollinsMenu/>
                </div>
            </div>
        </div>

        <div class="main-inner min-vh-100" v-if="!showApplicationForm">
            <div class="card card--info w-md-75 mx-auto" v-if="info.company_description.length">
                <div class="d-flex">
                    <font-awesome-icon class="fa-2x" :icon="['fa', 'info-circle']" />
                    <div class="pl-3">
                        <h4 class="heading4">About {{ info.company_name }}</h4>
                        <div v-html="info.company_description"></div>
                    </div>
                </div>
            </div>

            <div class="public-content w-md-75 mx-auto">
                <div class="d-flex justify-content-between align-items-center mb-4">
                    <h3 class="heading3">{{ info.name }}</h3>

                    <button
                        v-if="applicationStatus === 'applied' && info.allow_delete"
                        class="btn" @click="deleteSubmission"
                    >
                        Delete Submission
                    </button>
                </div>

                <div v-html="info.description"></div>

                <p v-if="info.categories.length">
                    <b>Categories:</b>
                    {{ info.categories.join(', ') }}
                </p>
                <p v-if="info.start_date && info.end_date">
                    <b>Timeline:</b> {{ decodeDate(info.start_date, ['month-str']) }} - {{ decodeDate(info.end_date, ['month-str']) }}
                </p>

                <template v-if="info.submitter_fields.length">
                    <h5 class="heading5 heading5--secondary mr-4 mb-0">Tracker Details</h5>

                    <div v-for="(field, index) in info.submitter_fields" :key="index" class="mb-4">
                        <h5 class="heading5 heading5--secondary">{{field.field.name}}</h5>

                        <CustomFieldValue
                            :field="field.field"
                            :init-value="field.value"
                            :editable="false"
                        />
                    </div>
                </template>

                <template v-if="!fromPreview && applicationStatus === 'applied'">
                    <div class="confirmation-send">Your application has been submitted to the {{ info.name }}</div>

                    <AnswersView
                        ref="answersView"
                        :tracker-id="trackerId"
                    />
                </template>

                <div v-if="fromPreview || applicationStatus !== 'applied'" class="text-center pt-3" style="margin-bottom: 50px">
                    <a @click="applyClicked" class="btn btn-primary btn-lg btn--apply">Apply</a>
                </div>
            </div>
        </div>

        <template v-else>
            <div class="main-inner min-vh-100">
                <div class="w-50 mx-auto">
                    <h3 class="heading3 mb-5">{{ info.name }}</h3>

                    <TrackerQuestionsFill
                        ref="trackerQuestionsFill"
                        :tracker-id="trackerId"
                        :from-preview="fromPreview"
                        @success="sendApplication"
                        @failed="sendingApplication = false"
                        @answerChanged="saveAnswer"
                    />

                    <div v-if="info.has_terms && !loadingQuestions" class="input-checkbox mt-3">
                        <input id="terms-agree" type="checkbox" v-model="agreed">
                        <label for="terms-agree">I have read and agree to the Challenge <a @click="showTermsModal = true" class="ml-1">Terms & Conditions</a></label>
                    </div>

                    <div v-if="!fromPreview && !loadingQuestions" class="text-center pt-3">
                        <button @click="apply" class="btn btn-primary btn-lg" :disabled="(info.has_terms && !agreed) || sendingApplication">Submit Application</button>
                    </div>
                </div>
            </div>
        </template>

        <b-modal v-model="showProfileModal" :hide-footer="true" modal-class="modal-w-lg" title="Complete Your Company Profile">
            <ProfileInfoForm
                :from-tracker="true"
                @saved="profileSaved"
            />
        </b-modal>

        <b-modal v-model="showTermsModal" :hide-footer="true" modal-class="modal--terms modal-w-xxl" title="">
            <template #modal-title>
                <div class="modal-top">
                    <h2 class="heading2 mb-0">Terms and Conditions</h2>
                </div>
            </template>

            <div v-html="info.terms"></div>
        </b-modal>
    </div>
</template>

<style scoped>
</style>
